import { BookOpen, Calendar, CheckSquare, ChevronDown, ChevronUp, Pencil, Sparkles, Square } from "lucide-react";
import { useMemo, useState } from "react";
import { Pressable, StyleSheet, View, Text, Platform } from "react-native";
import { colors, semanticColors, spacing, radii, typographyScale } from "../../../../styles/design-system";
import { Todo } from "../../../../graphql/generated/graphql";

interface TodoCardProps {
  toggleCompletion: (id: string, value: boolean) => void;
  discussTask?: (id: string) => void;
  onEdit?: (todo: Todo) => void;
  todo: Todo;
  workspaceName: string;
}

export const TodoCard = ({ toggleCompletion, discussTask, onEdit, todo, workspaceName }: TodoCardProps) => {
  const { name, completed, date, aiSummary, id } = todo;
  const [isActive, setIsActive] = useState(Platform.OS === "web" ? false : true);
  const [isDiscussActive, setIsDiscussActive] = useState(Platform.OS === "web" ? false : true);
  const [isEditActive, setIsEditActive] = useState(Platform.OS === "web" ? false : true);
  const shouldShowExpand = useMemo(() => (aiSummary?.length ?? 0) > 180, [aiSummary]);
  const [expandSummary, setExpandSummary] = useState(!shouldShowExpand);

  const displayedSummary = useMemo(
    () => (!shouldShowExpand || expandSummary ? aiSummary : aiSummary?.substring(0, 150) + "..."),
    [shouldShowExpand, expandSummary, aiSummary]
  );

  const containerStyle = [styles.container, isActive && !completed && styles.activeCard];
  const cardNameStyle = [styles.cardName, completed && styles.isCompleteCardName];
  const discussTaskBtnStyle = [
    styles.discussTaskBtn,
    isActive && !completed && styles.showDiscussBtn,
    isDiscussActive && styles.activeDiscussBtn,
  ];
  const editBtnStyle = [styles.editBtn, isActive && styles.showEditBtn, isEditActive && styles.activeEditBtn];

  const aiIconStyle = {
    ...styles.aiSummaryIcon,
    ...(completed ? styles.completedAISummaryIcon : {}),
  };

  const iconStyle = {
    ...styles.metaDataIcon,
    ...(completed ? styles.textLight : {}),
  };

  return (
    <View
      style={containerStyle}
      role="listitem"
      onPointerEnter={() => setIsActive(true)}
      onPointerLeave={() => setIsActive(false)}>
      <Pressable
        style={styles.completeButton}
        accessibilityRole="button"
        accessibilityLabel={`Mark '${name}' as ${completed ? "incomplete" : "complete"}`}
        accessibilityHint="Toggle the task completion status"
        accessibilityState={{ checked: completed }}
        role="button"
        aria-label={`Mark '${name}' as ${completed ? "incomplete" : "complete"}`}
        aria-pressed={completed}
        onPress={() => toggleCompletion(id, !completed)}>
        {completed ? (
          <CheckSquare size={20} style={styles.checkSquare} />
        ) : (
          <Square size={20} style={{ color: colors.grey[400] }} />
        )}
      </Pressable>
      <View style={styles.cardContentWrapper}>
        <Text style={cardNameStyle}>{name}</Text>
        {!!aiSummary && (
          <View style={styles.aiSummaryWrapper}>
            <Sparkles size={14} style={aiIconStyle} />
            <Text style={[styles.aiSummaryText, completed && styles.completedAISummaryText]}>
              {displayedSummary}
              {shouldShowExpand && (
                <Text>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Expand task summary"
                    accessibilityHint="Expand the task summary"
                    accessibilityState={{ expanded: expandSummary }}
                    role="button"
                    aria-label="Expand task summary"
                    aria-expanded={expandSummary}
                    onPress={() => setExpandSummary((prev) => !prev)}>
                    <Text style={styles.viewSummaryText}>
                      {expandSummary ? "Show Less" : "View Summary"}
                      {expandSummary ? (
                        <ChevronUp style={styles.viewSummaryText} size={12} />
                      ) : (
                        <ChevronDown style={styles.viewSummaryText} size={12} />
                      )}
                    </Text>
                  </Pressable>
                </Text>
              )}
            </Text>
          </View>
        )}
        <View style={styles.metaData}>
          {workspaceName != null && (
            <>
              <BookOpen size={14} style={iconStyle} />
              <Text style={[styles.metaDataText, completed && styles.textLight]}>{workspaceName}</Text>
            </>
          )}
          {workspaceName != null && date != null && (
            <View style={[styles.separator, completed && styles.backgroundTextLight]} />
          )}
          {date != null && (
            <>
              <Calendar size={14} style={iconStyle} />
              <Text style={styles.metaDataText}>
                {new Date(date).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "2-digit" })}
              </Text>
            </>
          )}
        </View>
      </View>
      <View style={styles.actionColumn}>
        {onEdit != null && todo != null && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Edit task: ${name}`}
            accessibilityHint="Edit the task"
            onPress={() => onEdit(todo)}
            style={editBtnStyle}
            onPointerEnter={() => setIsEditActive(true)}
            onPointerLeave={() => setIsEditActive(false)}>
            <Pencil size={16} style={styles.editIcon} />
          </Pressable>
        )}
        {!completed && discussTask != null && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Discuss task"
            accessibilityHint="Discuss the task"
            role="button"
            aria-label="Discuss task"
            onPress={() => discussTask(id)}
            style={discussTaskBtnStyle}
            onPointerEnter={() => setIsDiscussActive(true)}
            onPointerLeave={() => setIsDiscussActive(false)}>
            <Text style={styles.discussTaskText}>Discuss Task</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    display: "flex",
    flexDirection: "row",
    gap: spacing.sm,
    padding: spacing.sm,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    maxWidth: 900,
    backgroundColor: colors.white,
  },
  activeCard: {
    shadowColor: colors.grey[900],
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.16,
    shadowRadius: 3,
    elevation: 3,
    borderColor: colors.grey[300],
  },
  completeButton: {
    height: 20,
  },
  checkSquare: {
    color: colors.brand[500],
  },
  cardContentWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  cardName: {
    color: colors.grey[900],
    fontWeight: 600,
    fontSize: typographyScale.textSm.fontSize,
  },
  isCompleteCardName: {
    textDecorationLine: "line-through",
    textDecorationStyle: "solid",
    color: colors.grey[500],
  },
  aiSummaryWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.xs,
  },
  aiSummaryIcon: {
    color: "#a855f7",
    marginTop: spacing.xxs,
  },
  completedAISummaryIcon: {
    color: "#c084fc",
    marginTop: spacing.xxs,
  },
  aiSummaryText: {
    flex: 1,
    color: colors.grey[600],
    fontSize: 13,
    lineHeight: 21,
  },
  completedAISummaryText: {
    color: colors.grey[500],
  },
  viewSummaryText: {
    marginLeft: 6,
    color: colors.brand[500],
  },
  metaData: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  separator: {
    marginHorizontal: 6,
    width: spacing.xxs,
    height: spacing.xxs,
    borderRadius: radii.xs,
    backgroundColor: colors.grey[300],
  },
  metaDataIcon: {
    color: colors.grey[600],
  },
  metaDataText: {
    color: colors.grey[600],
    fontSize: typographyScale.textXs.fontSize,
  },
  actionColumn: {
    display: "flex",
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
  editBtn: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: semanticColors.surface.white,
    opacity: 0,
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  showEditBtn: {
    opacity: 1,
    pointerEvents: "auto",
  },
  activeEditBtn: {
    backgroundColor: semanticColors.surface.light,
  },
  editIcon: {
    color: "#344054",
  },
  discussTaskBtn: {
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.sm,
    backgroundColor: semanticColors.surface.white,
    opacity: 0,
    pointerEvents: "none",
  },
  showDiscussBtn: {
    opacity: 1,
    pointerEvents: "auto",
  },
  activeDiscussBtn: {
    backgroundColor: semanticColors.surface.light,
  },
  discussTaskText: {
    color: colors.grey[700],
    fontSize: typographyScale.textXs.fontSize,
    fontWeight: 500,
  },
  textLight: {
    color: colors.grey[500],
  },
  backgroundTextLight: {
    backgroundColor: colors.grey[500],
  },
});
